import type { CompressedTexture } from 'three'
import {
  Box3,
  ClampToEdgeWrapping,
  DoubleSide,
  Group,
  LinearFilter,
  LinearMipmapLinearFilter,
  LinearMipmapNearestFilter,
  LoadingManager,
  Mesh,
  MirroredRepeatWrapping,
  NearestFilter,
  NearestMipmapLinearFilter,
  NearestMipmapNearestFilter,
  RepeatWrapping,
  Texture,
} from 'three'
import type { GLTFParser } from 'three/addons/loaders/GLTFLoader.js'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import type {
  TileDecodeContext,
  TileResourceCosts,
  TileResourceData,
  TileTextureQualityOptions,
  TileTextureQualityPolicy,
} from '@/plugins/tiles/contracts'
import { decodeTileImage, resizeTileImage } from '@/plugins/tiles/mesh/decodeTileImage'
import { createGltfDependencyReader } from '@/plugins/tiles/mesh/gltfDependencyReader'
import {
  estimateGltfGeometryBytes,
  estimateManifestCosts,
  readGltfManifest,
  readImageInfo,
} from '@/plugins/tiles/mesh/gltfManifest'
import type { MeshTileContentPackage } from '@/plugins/tiles/mesh/meshContentResources'
import { MeshContentResources } from '@/plugins/tiles/mesh/meshContentResources'
import type { DecodeMeshopt } from '@/plugins/tiles/mesh/meshoptViews'
import { createBufferViewResolver, MESHOPT_EXTENSION } from '@/plugins/tiles/mesh/meshoptViews'
import { TileImageDecodeService } from '@/plugins/tiles/mesh/TileImageDecodeService'
import { TileTextureQualityUpdater } from '@/plugins/tiles/mesh/TileTextureQualityUpdater'
import type { TextureSizing } from '@/plugins/tiles/mesh/textureSizing'
import { getTextureDimensions, resolveTextureSizing } from '@/plugins/tiles/mesh/textureSizing'
import type {
  ITileImageDecodeService,
  TileImageDecodeServiceOptions,
} from '@/plugins/tiles/mesh/tileImageProtocol'
import { TilesError, throwIfTilesAborted } from '@/plugins/tiles/TilesError'
import type { IDracoDecodeService } from '@/shared/draco/DracoDecodeService'
import { installDracoGltfBridge } from '@/shared/draco/gltfBridge'

export interface GltfContentDecoderOptions {
  readonly textureQuality?: TileTextureQualityOptions
  /** 默认 worker；主线程兼容必须显式选择。既有 decodeImage 注入保持优先。 */
  readonly imageProcessing?: 'worker' | 'main-thread'
  /** 默认借用；仅显式 ownsImageService=true 时由 decoder 销毁。 */
  readonly imageService?: ITileImageDecodeService
  readonly ownsImageService?: boolean
  readonly imageWorker?: TileImageDecodeServiceOptions
  /** 显式借用共享 Draco 服务；内容包不销毁服务。 */
  readonly draco?: IDracoDecodeService
  /** 显式借用 Meshopt 解码能力；返回独占输出，包销毁不会终止共享 decoder。 */
  readonly decodeMeshopt?: DecodeMeshopt
  /** 默认拒绝元数据扩展；geometry-only 仅预览可独立绘制的可选元数据内容。 */
  readonly optionalMetadataPolicy?: 'reject' | 'geometry-only'
  /** 显式启用二维 KTX2；返回独占纹理，包负责销毁。共享转码器由宿主管理。
   * target 为按纹理质量计算的上限，宿主须选择不超过该尺寸的 mip。
   */
  readonly decodeKtx2?: (
    data: ArrayBuffer,
    signal: AbortSignal,
    target: Readonly<{ width: number; height: number }>
  ) => Promise<CompressedTexture>
  /** 强制所有 Mesh 材质双面绘制；默认 false，遵循 glTF 的 doubleSided。 */
  readonly forceDoubleSided?: boolean
  /** 图片上传最大边长，正整数，默认1024；保持宽高比，不放大小图。 */
  readonly maxTextureSize?: number
  /** 原图尺寸缩放比例，(0,1]，默认1；与最大边长限制共同生效。 */
  readonly textureScale?: number
  /** 注入器返回的图片由内容包独占；ImageBitmap 最终由该包 close。 */
  readonly decodeImage?: (
    data: ArrayBuffer,
    mimeType: string,
    signal: AbortSignal,
    target?: Readonly<{ width: number; height: number }>
  ) => Promise<TexImageSource>
}

export interface TileMeshPrimitiveSource {
  readonly assetId: string
  readonly tileId: string
  readonly contentId: string
  readonly sourceUrl: string
  readonly nodeIndex: number
  readonly meshIndex: number
  readonly primitiveIndex: number
  readonly id: string
}

const FILTERS = {
  9728: NearestFilter,
  9729: LinearFilter,
  9984: NearestMipmapNearestFilter,
  9985: LinearMipmapNearestFilter,
  9986: NearestMipmapLinearFilter,
  9987: LinearMipmapLinearFilter,
} as const
const WRAPPINGS = {
  33071: ClampToEdgeWrapping,
  33648: MirroredRepeatWrapping,
  10497: RepeatWrapping,
} as const

/** 保留几何与节点拓扑；原 buffer（含内嵌图片）仍按实际输入计费。 */
function stripCollisionAppearance(json: any): void {
  json.images = []
  json.textures = []
  json.samplers = []
  json.materials = (json.materials ?? []).map((material: any) => ({
    name: material.name,
    doubleSided: material.doubleSided,
  }))
}

function enrich(error: unknown, context: TileDecodeContext, url: string): TilesError {
  return new TilesError(
    {
      code: context.signal.aborted ? 'cancelled' : 'invalid-resource',
      stage: 'decode',
      retryable: false,
      message: error instanceof Error ? error.message : String(error),
      ...(error instanceof TilesError ? error.diagnostic : {}),
      ...context.source,
      sourceUrl: url,
    },
    { cause: error }
  )
}

/** M0 静态 glTF 解码；Three 内部不会收到可自行下载的外部 URI。 */
export class GltfContentDecoder {
  private readonly _textureSizing: TextureSizing
  private _imageService?: ITileImageDecodeService
  private _disposed = false
  readonly qualityPolicy: TileTextureQualityPolicy
  readonly qualityUpdater: TileTextureQualityUpdater

  constructor(private readonly _options: GltfContentDecoderOptions = {}) {
    this._textureSizing = resolveTextureSizing(_options)
    this._imageService = _options.imageService
    const quality = _options.textureQuality ?? { mode: 'fixed' }
    if (!['fixed', 'adaptive'].includes(quality.mode))
      throw new RangeError('纹理质量模式必须为 fixed 或 adaptive')
    const stableMs = quality.mode === 'adaptive' ? (quality.stableMs ?? 500) : 500
    const cooldownMs = quality.mode === 'adaptive' ? (quality.cooldownMs ?? 2000) : 2000
    if (![stableMs, cooldownMs].every((value) => Number.isFinite(value) && value >= 0))
      throw new RangeError('纹理质量观察与冷却时间必须有限且非负')
    this.qualityPolicy = Object.freeze({
      mode: quality.mode,
      maxTextureSize: this._textureSizing.maxTextureSize,
      stableMs,
      cooldownMs,
    })
    this.qualityUpdater = new TileTextureQualityUpdater(_options, (bytes, info, target, context) =>
      this.decodeImage(bytes, info, target, context)
    )
    if (
      _options.imageProcessing !== undefined &&
      !['worker', 'main-thread'].includes(_options.imageProcessing)
    )
      throw new RangeError('imageProcessing 必须为 worker 或 main-thread')
  }

  getImageStatus() {
    if (this._options.decodeImage) return { path: 'custom-compatible' as const }
    if (this._options.imageProcessing === 'main-thread') return { path: 'main-thread' as const }
    return (
      this._imageService?.getStatus() ?? {
        path: 'worker' as const,
        queued: 0,
        active: 0,
        activePeakBytes: 0,
        workerCount: 0,
        failures: 0,
        closed: this._disposed,
      }
    )
  }

  dispose(): void {
    if (this._disposed) return
    this._disposed = true
    if (!this._options.imageService || this._options.ownsImageService) this._imageService?.dispose()
  }

  estimate(
    resource: TileResourceData,
    purpose?: 'display' | 'collision',
    maxTextureSize?: number
  ): TileResourceCosts {
    try {
      const manifest = readGltfManifest(
        resource,
        !!this._options.decodeKtx2,
        !!this._options.decodeMeshopt,
        this._options.optionalMetadataPolicy,
        !!this._options.draco
      )
      if (purpose === 'collision') stripCollisionAppearance(manifest.json)
      return estimateManifestCosts(resource, manifest, this.sizing(maxTextureSize))
    } catch (error) {
      if (error instanceof TilesError)
        throw new TilesError({ ...error.diagnostic, sourceUrl: resource.url }, { cause: error })
      throw error
    }
  }

  async decode(
    resource: TileResourceData,
    context: TileDecodeContext
  ): Promise<MeshTileContentPackage> {
    if (this._disposed)
      throw new TilesError({ code: 'disposed', stage: 'decode', message: '图片解码器已销毁' })
    const owner = new MeshContentResources()
    const sizing = this.sizing(context.maxTextureSize)
    let views: ReturnType<typeof createBufferViewResolver> | undefined
    const abort = () => owner.disposeSafely()
    context.signal.addEventListener('abort', abort, { once: true })
    try {
      throwIfTilesAborted(context.signal)
      const manifest = readGltfManifest(
        resource,
        !!this._options.decodeKtx2,
        !!this._options.decodeMeshopt,
        this._options.optionalMetadataPolicy,
        !!this._options.draco
      )
      const { json } = manifest
      if (context.purpose === 'collision') stripCollisionAppearance(json)
      context.reserveCosts?.(estimateManifestCosts(resource, manifest, sizing))
      const dependencies = createGltfDependencyReader(resource, context)
      const buffers = await dependencies.readBuffers(manifest)
      views = createBufferViewResolver(json, buffers, context.signal, this._options.decodeMeshopt)
      const resolveView = views.resolve
      const imageBytes = await Promise.all(
        (json.images ?? []).map((image: any) =>
          image.uri !== undefined ? dependencies.read(image.uri) : resolveView(image.bufferView)
        )
      )
      const inputBytes = dependencies.inputBytes
      const imageInfo = imageBytes.map((bytes, index) => {
        const info = readImageInfo(bytes)
        if (info.mimeType === 'image/ktx2' && !this._options.decodeKtx2)
          throw new Error('KTX2 需要显式装配 decodeKtx2')
        if (json.images[index].mimeType && json.images[index].mimeType !== info.mimeType)
          throw new Error('glTF image MIME 与容器不一致')
        return info
      })
      const geometryEstimate = estimateGltfGeometryBytes(json, !!this._options.draco)
      const imageTargets = imageInfo.map((info) => getTextureDimensions(info, sizing))
      const decodedImageBytes = imageInfo.reduce(
        (sum, image) => sum + image.width * image.height * 4,
        0
      )
      const textureEstimate = (json.textures ?? []).reduce((sum: number, texture: any) => {
        const info = imageTargets[texture.source]
        if (!info) throw new Error('glTF texture.source 无效')
        return sum + Math.ceil((info.width * info.height * 4 * 4) / 3)
      }, 0)
      // 驻留只计缩小后的位图；浏览器内部/兼容回退仍可能短暂持有原尺寸解码像素。
      const resizedImageBytes = imageTargets.reduce(
        (sum, image) => sum + image.width * image.height * 4,
        0
      )
      // 自定义解码器可能返回ImageData；其兼容缩放还会临时创建一份原尺寸画布。
      const transientBytes =
        inputBytes * 2 +
        geometryEstimate +
        manifest.meshoptBytes * 3 +
        (manifest.dracoBytes ?? 0) +
        decodedImageBytes * 2 +
        resizedImageBytes * 2
      context.reserveCosts?.({
        memory: { cpuBytes: transientBytes, gpuBytes: geometryEstimate + textureEstimate },
        inputBytes,
        geometryBytes: geometryEstimate,
        textureBytes: textureEstimate,
        transientBytes,
        cpuTextureBytes: resizedImageBytes,
        gpuTextureBytes: textureEstimate,
      })
      throwIfTilesAborted(context.signal)
      const images = new Map<number, Promise<TexImageSource>>()
      const manager = new LoadingManager()
      // 即使未来 Three 新增某项隐式依赖，也禁止其绕开 Tiles 数据源。
      manager.setURLModifier(() => {
        throw new Error('glTF 未受控资源请求被拒绝')
      })
      const loader = new GLTFLoader(manager)
      let activeParser: GLTFParser | undefined
      loader.register((parser) => {
        activeParser = parser
        parser.loadBuffer = async (index) => {
          throwIfTilesAborted(context.signal)
          if (!buffers[index]) throw new Error('glTF buffer 引用无效')
          return buffers[index]!.slice().buffer
        }
        parser.loadBufferView = resolveView
        const loadGeometries = parser.loadGeometries.bind(parser)
        parser.loadGeometries = (primitives) =>
          Promise.all(
            primitives.map((primitive) =>
              loadGeometries([primitive]).then((geometries) => owner.track(geometries))
            )
          ).then((groups) => groups.flat())
        const getDependency = parser.getDependency.bind(parser)
        parser.getDependency = (type, index) =>
          getDependency(type, index).then((value) => owner.track(value))
        const assignFinalMaterial = parser.assignFinalMaterial.bind(parser)
        parser.assignFinalMaterial = (mesh) => {
          try {
            assignFinalMaterial(mesh)
          } finally {
            owner.track(mesh)
          }
        }
        return {
          name: 'SHARE3D_tiles_controlled_resources',
          loadTexture: async (index) => {
            const definition = json.textures[index]
            const source = definition.source
            const info = imageInfo[source]
            if (!info) throw new Error('glTF 图片引用无效')
            let texture: Texture
            if (info.mimeType === 'image/ktx2') {
              const compressed = owner.track(
                await this._options.decodeKtx2!(
                  imageBytes[source],
                  context.signal,
                  imageTargets[source]
                )
              )
              throwIfTilesAborted(context.signal)
              const target = imageTargets[source]
              if (compressed.image.width > target.width || compressed.image.height > target.height)
                throw new Error('KTX2 解码器返回尺寸超过纹理质量上限')
              texture = compressed
            } else {
              let image = images.get(source)
              if (!image) {
                const target = imageTargets[source]
                image = this.decodeImage(imageBytes[source], info, target, context).then(
                  (decoded) => owner.trackImage(decoded)
                )
                context.trackDecode?.(image)
                images.set(source, image)
              }
              const decoded = await image
              throwIfTilesAborted(context.signal)
              texture = owner.track(new Texture(decoded))
            }
            const sampler = json.samplers?.[definition.sampler] ?? {}
            texture.flipY = false
            texture.magFilter = sampler.magFilter === 9728 ? NearestFilter : LinearFilter
            texture.minFilter =
              FILTERS[sampler.minFilter as keyof typeof FILTERS] ?? LinearMipmapLinearFilter
            texture.wrapS = WRAPPINGS[sampler.wrapS as keyof typeof WRAPPINGS] ?? RepeatWrapping
            texture.wrapT = WRAPPINGS[sampler.wrapT as keyof typeof WRAPPINGS] ?? RepeatWrapping
            texture.generateMipmaps =
              info.mimeType !== 'image/ktx2' &&
              texture.minFilter !== NearestFilter &&
              texture.minFilter !== LinearFilter
            if (
              info.mimeType === 'image/ktx2' &&
              texture.mipmaps.length < 2 &&
              texture.minFilter !== NearestFilter
            )
              texture.minFilter = LinearFilter
            texture.name = definition.name ?? json.images[source].name ?? ''
            texture.userData.tilesTextureIndex = index
            texture.needsUpdate = true
            parser.associations.set(texture, { textures: index })
            return texture
          },
        }
      })
      if (this._options.draco)
        installDracoGltfBridge(loader, this._options.draco, {
          signal: context.signal,
          source: resource.url,
          onGeometry: (geometry) => {
            owner.track(geometry)
          },
          onDecode: (pending) => context.trackDecode?.(pending),
        })
      // 工作副本仅移除由本层接管的压缩声明，保留量化和其他已验证能力。
      const rendererJson = {
        ...json,
        extensionsUsed: json.extensionsUsed.filter((name: string) => name !== MESHOPT_EXTENSION),
        extensionsRequired: json.extensionsRequired?.filter(
          (name: string) => name !== MESHOPT_EXTENSION
        ),
        bufferViews: json.bufferViews?.map((view: any) => ({ ...view, extensions: undefined })),
        buffers: json.buffers?.map((buffer: any) => ({ ...buffer, extensions: undefined })),
      }
      const gltf = await loader.parseAsync(JSON.stringify(rendererJson), '')
      for (const scene of gltf.scenes) owner.track(scene)
      throwIfTilesAborted(context.signal)
      const root = new Group()
      root.name = context.source.contentId
      root.rotation.x = Math.PI / 2
      root.add(gltf.scene)
      root.updateMatrixWorld(true)
      let meshCount = 0
      gltf.scene.traverse((object) => {
        if (!(object instanceof Mesh)) return
        meshCount++
        if (this._options.forceDoubleSided) {
          const materials = Array.isArray(object.material) ? object.material : [object.material]
          for (const material of materials) material.side = DoubleSide
        }
        const association = activeParser?.associations.get(object) as
          | { meshes?: number; primitives?: number; nodes?: number }
          | undefined
        let node: typeof object | import('three').Object3D | null = object
        let nodeIndex = association?.nodes
        while (node && nodeIndex === undefined) {
          nodeIndex = activeParser?.associations.get(node)?.nodes
          node = node.parent
        }
        if (
          nodeIndex === undefined ||
          association?.meshes === undefined ||
          association.primitives === undefined
        )
          throw new Error('glTF primitive 缺少稳定的源索引')
        const source: TileMeshPrimitiveSource = Object.freeze({
          ...context.source,
          sourceUrl: resource.url,
          nodeIndex,
          meshIndex: association.meshes,
          primitiveIndex: association.primitives,
          id: `${context.source.contentId}/nodes/${nodeIndex}/meshes/${association.meshes}/primitives/${association.primitives}`,
        })
        object.userData.tilesSource = source
        const position = object.geometry.getAttribute('position')
        const indices = object.geometry.getIndex()
        if (!position) throw new Error('解码后 Mesh 缺少 POSITION')
        for (let i = 0; i < position.count; i++)
          if (![position.getX(i), position.getY(i), position.getZ(i)].every(Number.isFinite))
            throw new Error('glTF POSITION 包含非有限数')
        if (indices)
          for (let i = 0; i < indices.count; i++)
            if (indices.getX(i) >= position.count) throw new Error('glTF 索引越出 POSITION 范围')
      })
      if (!meshCount) throw new Error('默认 glTF scene 不包含可绘制 Mesh')
      root.userData.tilesWarnings = [...manifest.warnings]
      const boundingBox = new Box3().setFromObject(root)
      const costs = owner.costs()
      // 解码栈和输入在返回后才退出；预约保留峰值，cache admit 再切换到包的稳态成本。
      context.reserveCosts?.({
        memory: {
          cpuBytes: Math.max(transientBytes, costs.memory?.cpuBytes ?? 0),
          gpuBytes: Math.max(geometryEstimate + textureEstimate, costs.memory?.gpuBytes ?? 0),
        },
        inputBytes: Math.max(inputBytes, costs.inputBytes),
        geometryBytes: Math.max(geometryEstimate, costs.geometryBytes),
        textureBytes: Math.max(textureEstimate, costs.textureBytes),
        transientBytes,
        cpuTextureBytes: Math.max(resizedImageBytes, costs.cpuTextureBytes ?? 0),
        gpuTextureBytes: Math.max(textureEstimate, costs.gpuTextureBytes ?? 0),
      })
      let qualityVersion = 0
      let qualitySize = sizing.maxTextureSize
      const content: MeshTileContentPackage = {
        adapterId: 'gltf-mesh',
        source: context.source,
        object3D: root,
        boundingBox,
        get costs() {
          return owner.costs()
        },
        get textureQuality() {
          return Object.freeze({ maxTextureSize: qualitySize, version: qualityVersion })
        },
        get disposed() {
          return owner.disposed
        },
        onDispose: (listener) => owner.onDispose(listener),
        createRetirement: () => owner.createRetirement(),
        dispose: () => owner.dispose(),
      }
      if (this.qualityPolicy.mode === 'adaptive' && context.purpose !== 'collision')
        this.qualityUpdater.register(content, {
          owner,
          imageInfo,
          inputBytes,
          textureSources: (json.textures ?? []).map((texture: any) => texture.source),
          setQuality: (size) => {
            qualitySize = size
            qualityVersion++
          },
        })
      return content
    } catch (error) {
      owner.disposeSafely()
      throw enrich(error, context, resource.url)
    } finally {
      views?.clear()
      context.signal.removeEventListener('abort', abort)
    }
  }

  private sizing(maxTextureSize?: number): TextureSizing {
    return maxTextureSize === undefined
      ? this._textureSizing
      : resolveTextureSizing({
          ...this._textureSizing,
          maxTextureSize: Math.min(maxTextureSize, this._textureSizing.maxTextureSize),
        })
  }

  private decodeImage(
    bytes: ArrayBuffer,
    info: ReturnType<typeof readImageInfo>,
    target: { width: number; height: number },
    context: TileDecodeContext
  ): Promise<TexImageSource> {
    if (this._options.decodeImage)
      return this._options
        .decodeImage(bytes, info.mimeType, context.signal, target)
        .then((decoded) => resizeTileImage(decoded, target, context.signal))
    if (this._options.imageProcessing === 'main-thread')
      return decodeTileImage(bytes, info.mimeType, context.signal, target)
    return (this._imageService ??= new TileImageDecodeService(this._options.imageWorker)).decode({
      data: bytes,
      mimeType: info.mimeType,
      sourceSize: info,
      target,
      signal: context.signal,
    })
  }
}
